import { NotFoundError } from '@cian/peperrors';

import { ResponseError } from '../../errors';
import { InvalidLocationError } from '../../errors/InvalidLocationError';
import { prepareOffers } from '../../mappers/prepareOffers';
import {
  fetchSearchOffersDesktop,
  TSearchOffersDesktopResponse,
} from '../../repositories/search-offers/v2/search-offers-desktop';
import { IApplicationContext } from '../../types/applicationContext';
import { IOffersData } from '../../types/offersData';
import { getListingTelemetryKey } from '../../utils/telemetry';

/** Номер первой страницы выдачи: на ней же jsonQuery может вовсе не содержать `page` */
const FIRST_PAGE = 1;

enum ESellersSpecialProjectTelemetry {
  LoadStart = 'serp.sellers-special-project.load.start',
  LoadSuccess = 'serp.sellers-special-project.load.success',
  LoadError = 'serp.sellers-special-project.load.error',
}

interface IFetchOffersPathParameters {
  subdomain: string;
  path: string;
}

interface IFetchOffersJsonQueryParameters {
  // oxlint-disable-next-line typescript/no-explicit-any
  jsonQuery: any;
}

interface IFetchOffersLiquiditySourceParameters {
  liquiditySource?: string;
}

type TFetchOffersParameters = (IFetchOffersPathParameters | IFetchOffersJsonQueryParameters) &
  IFetchOffersLiquiditySourceParameters;

export async function fetchOffers(
  { httpApi, telemetry }: IApplicationContext,
  _parameters: TFetchOffersParameters,
): Promise<IOffersData> {
  const parameters =
    'jsonQuery' in _parameters
      ? { jsonQuery: _parameters.jsonQuery }
      : { _subdomain: _parameters.subdomain, _path: _parameters.path };

  if (_parameters.liquiditySource) {
    Object.assign(parameters, { _liquiditySource: _parameters.liquiditySource });
  }

  telemetry.increment(ESellersSpecialProjectTelemetry.LoadStart);

  const offersData: TSearchOffersDesktopResponse = await fetchSearchOffersDesktop({
    httpApi,
    parameters,
  });

  if (offersData.statusCode !== 200) {
    // oxlint-disable-next-line typescript/no-explicit-any
    if ((offersData.response.errors?.[0] as any)?.code === 'urlNotFound') {
      telemetry.increment(ESellersSpecialProjectTelemetry.LoadError);
      throw new NotFoundError({
        domain: 'fetchOffers',
        message: 'URL not found',
        details: {
          errors: JSON.stringify(offersData.response.errors),
        },
      });
    }

    telemetry.increment(ESellersSpecialProjectTelemetry.LoadError);
    throw new ResponseError({
      domain: 'fetchOffers',
      message: offersData.response.message || '',
      details: {
        errors: JSON.stringify(offersData.response.errors),
      },
    });
  }

  if (offersData.response.data.redirectData) {
    const { redirectData } = offersData.response.data;

    if (!redirectData.location || !redirectData.statusCode) {
      telemetry.increment(ESellersSpecialProjectTelemetry.LoadError);
      throw new ResponseError({
        domain: 'fetchOffers',
        message: 'Неверный формат redirectData',
        details: {
          // oxlint-disable-next-line typescript/no-explicit-any
          redirectData: redirectData as any,
        },
      });
    }

    telemetry.increment(ESellersSpecialProjectTelemetry.LoadError);
    throw new InvalidLocationError({
      domain: 'fetchOffers',
      message: 'В ответе API пришёл редирект',
      details: {
        redirectStatusCode: redirectData.statusCode as 301 | 302,
        redirectLocation: redirectData.location,
      },
    });
  }

  const result = prepareOffers(offersData.response);

  /**
   * Доля нулевых выдач в разрезе вертикалей: считаем и все выдачи, и пустые, — отношение
   * даёт нужный процент. Считаем здесь, потому что через сервис проходят и серверный рендер,
   * и клиентские перезапросы выдачи.
   *
   * Только первая страница: пагинация ходит тем же сервисом, но по SLO учитывается лишь
   * первый основной запрос, иначе одна выдача попадёт в счётчик столько раз, сколько страниц
   * пролистали. Инфинити-скролл идёт отдельной ручкой и здесь не проходит.
   */
  if (!result.jsonQuery.page || result.jsonQuery.page.value === FIRST_PAGE) {
    telemetry.increment(getListingTelemetryKey(result.jsonQuery, result.offersSerialized.length === 0));
  }

  telemetry.increment(
    result.specialPromoSecondaryOffers
      ? ESellersSpecialProjectTelemetry.LoadSuccess
      : ESellersSpecialProjectTelemetry.LoadError,
  );

  return result;
}
